// src/components/ServisKayitlari.js

import React, { useState, useMemo } from 'react';
import { db } from '../firebase';
import { doc, deleteDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Box, IconButton, Tooltip, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, InputAdornment, Button, Stack, useTheme, useMediaQuery } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
import BuildIcon from '@mui/icons-material/Build';
import GlassCard from '../ui/glass/components/GlassCard';
import PageHeader from '../components/common/PageHeader';
import StatusChip from '../components/common/StatusChip';
import EmptyState from '../components/common/EmptyState';
import { Loading } from '../components/common/Loading';
import { formatCurrency } from '../utils/formatters';
import usePermissions from '../hooks/usePermissions';
import { exportToExcel } from '../utils/exportToExcel';
import useCachedCollection from '../hooks/useCachedCollection';

// Servis kayıtları Excel kolon tanımı (salt-okunur export)
const serviceExportColumns = [
  { header: 'Tarih', value: (r) => r.olusturmaTarihi, type: 'datetime', width: 18 },
  { header: 'Cihaz Seri No', value: (r) => r.cihazSeriNumarasi || r.aracPlaka || '', width: 18 },
  { header: 'Müşteri', value: (r) => r.musteriAdSoyad, width: 24 },
  { header: 'Durum', value: (r) => r.durum, width: 16 },
  { header: 'Toplam Tutar (₺)', value: (r) => r.toplamTutar, type: 'money', width: 16 },
  { header: 'Ödenen (₺)', value: (r) => r.odenenTutar, type: 'money', width: 14 },
  { header: 'Kalan (₺)', value: (r) => r.kalanTutar, type: 'money', width: 14 },
];


const getStatusChipColor = (status) => {
  switch (status) {
    case 'Beklemede': return 'warning';
    case 'İşlemde': return 'info';
    case 'Tamamlandı': return 'success';
    case 'Teslime Hazır': return 'secondary';
    case 'Teslim Edildi': return 'default';
    default: return 'default';
  }
};

const ServisKayitlari = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleCount, setVisibleCount] = useState(25); // Faz 7: ekranda gösterilen satır sayısı
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { canDelete } = usePermissions();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Faz 7: realtime onSnapshot yerine oturum-içi cache (oturumda 1 kez okunur,
  // ekranlar arası geçişte tekrar okunmaz). Silme sonrası refresh edilir.
  const { data: recordsRaw, loading, refresh: refreshRecords } = useCachedCollection('serviceRecords');
  // Eski Firestore orderBy("olusturmaTarihi","desc") sırasını koru (cache sırasız gelir).
  const serviceRecords = useMemo(() => {
    const toMs = (t) => (t?.toMillis ? t.toMillis() : (t?.seconds ? t.seconds * 1000 : 0));
    return [...recordsRaw].sort((a, b) => toMs(b.olusturmaTarihi) - toMs(a.olusturmaTarihi));
  }, [recordsRaw]);

  const handleDelete = async (id, seriNumarasi) => { // Değiştirildi
    if (window.confirm(`'${seriNumarasi}' seri numaralı cihaza ait servis kaydını silmek istediğinizden emin misiniz?`)) {
      try {
        await deleteDoc(doc(db, 'serviceRecords', id)); // Değiştirildi
        await refreshRecords(); // Faz 7: silinen kayıt listeden hemen düşsün
        alert("Servis kaydı başarıyla silindi.");
      } catch (e) { console.error("Servis kaydı silinirken hata: ", e); }
    }
  };

  const filteredRecords = serviceRecords.filter(record => // Değiştirildi
    (record.cihazSeriNumarasi && record.cihazSeriNumarasi.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (record.musteriAdSoyad || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Faz 7: ekranda yalnızca ilk `visibleCount` kaydı göster (DOM'u hafifletir).
  // Arama TÜM kayıtlar üzerinde çalışır; export da tüm filtreli veriyi indirir.
  const visibleRecords = filteredRecords.slice(0, visibleCount);

  // Excel export (salt-okunur) — aktif aramaya uyan servis kayıtlarını indirir
  const handleExportVisible = () => {
    if (filteredRecords.length === 0) {
      alert('Dışa aktarılacak servis kaydı bulunamadı.');
      return;
    }
    exportToExcel({ rows: filteredRecords, columns: serviceExportColumns, sheetName: 'Servis Kayıtları', fileName: 'Servis_Kayitlari', scopeLabel: 'Goruntulenen' });
  };

  // Excel export (salt-okunur) — aramadan bağımsız tüm servis kayıtlarını indirir
  const handleExportAll = () => {
    if (serviceRecords.length === 0) {
      alert('Dışa aktarılacak servis kaydı bulunamadı.');
      return;
    }
    exportToExcel({ rows: serviceRecords, columns: serviceExportColumns, sheetName: 'Servis Kayıtları', fileName: 'Servis_Kayitlari', scopeLabel: 'Tumu' });
  };

  return (
    <Box sx={{ p: { xs: 1, md: 1 } }}>
      <PageHeader
        title="Tüm Servis Kayıtları"
        subtitle="Cihaz servis işlemlerini buradan takip edebilirsiniz."
        icon={<BuildIcon />}
      >
            <TextField
              size="small"
              variant="outlined"
              placeholder="Seri No veya Müşteri Ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (<InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>)
              }}
              sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 1 }}
            />
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={refreshRecords}
              disabled={loading}
              sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              Yenile
            </Button>
            <Button
              variant="outlined"
              color="success"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportVisible}
              sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              Görüneni İndir
            </Button>
            <Button
              variant="outlined"
              color="success"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportAll}
              sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              Tümünü İndir
            </Button>
            <Button
              variant="contained"
              onClick={() => navigate('/servis-kaydi-olustur')}
              startIcon={<AddCircleOutlineIcon />}
              sx={{ flexShrink: 0, whiteSpace: 'nowrap', px: 3 }}
            >
              Servis Kaydı Oluştur
            </Button>
      </PageHeader>

      <GlassCard sx={{ width: '100%', overflow: 'hidden', p: 0, bgcolor: isMobile ? 'transparent' : undefined, boxShadow: isMobile ? 'none' : undefined, backdropFilter: isMobile ? 'none' : undefined, border: isMobile ? 'none' : undefined }}>
        {loading ? (
          <Loading message="Servis kayıtları yükleniyor..." />
        ) : isMobile ? (
          // MOBILE CARD VIEW
          <Stack spacing={2} sx={{ p: isMobile ? 0 : 2 }}>
            {visibleRecords.map((record) => (
              <GlassCard key={record.id} onClick={() => navigate(`/servis-kaydi/${record.id}`)} sx={{ p: 2, cursor: 'pointer', '&:active': { transform: 'scale(0.98)' }, transition: 'transform 0.1s' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">{record.cihazSeriNumarasi || record.aracPlaka || 'Seri No Yok'}</Typography>
                  <StatusChip label={record.durum} color={getStatusChipColor(record.durum)} />
                </Box>

                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="body2" color="text.secondary">{record.musteriAdSoyad}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    {record.olusturmaTarihi?.toDate().toLocaleDateString('tr-TR')}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                  <Typography variant="body2" sx={{ color: (record.kalanTutar || 0) > 0 ? 'error.main' : 'text.primary', fontWeight: 'bold' }}>
                    Kalan: {formatCurrency(record.kalanTutar)}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {currentUser && (currentUser.role === 'Yönetici' || currentUser.role === 'Ofis Personeli') && (
                      <IconButton size="small" color="info" onClick={(e) => { e.stopPropagation(); navigate(`/servis-kaydi/duzenle/${record.id}`); }}><EditIcon fontSize="small" /></IconButton>
                    )}
                    {canDelete() && (
                      <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(record.id, record.cihazSeriNumarasi); }}><DeleteIcon fontSize="small" /></IconButton>
                    )}
                  </Box>
                </Box>
              </GlassCard>
            ))}
            {filteredRecords.length === 0 && (
              <EmptyState
                icon={<BuildIcon />}
                message="Servis kaydı bulunamadı."
                description={searchTerm ? 'Aramanızla eşleşen kayıt yok.' : 'Yeni bir servis kaydı oluşturarak başlayın.'}
              />
            )}
            {visibleCount < filteredRecords.length && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 1 }}>
                <Typography variant="caption" color="text.secondary">{visibleRecords.length} / {filteredRecords.length} gösteriliyor</Typography>
                <Button variant="outlined" onClick={() => setVisibleCount(c => c + 25)}>Daha Fazla Yükle</Button>
              </Box>
            )}
          </Stack>
        ) : (
          // DESKTOP TABLE VIEW
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Cihaz Seri No</TableCell>
                  <TableCell>Müşteri</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell>Kayıt Tarihi</TableCell>
                  <TableCell align="right">Kalan Bakiye</TableCell>
                  <TableCell align="center">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRecords.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ border: 0 }}>
                      <EmptyState
                        icon={<BuildIcon />}
                        message="Servis kaydı bulunamadı."
                        description={searchTerm ? 'Aramanızla eşleşen kayıt yok.' : 'Yeni bir servis kaydı oluşturarak başlayın.'}
                      />
                    </TableCell>
                  </TableRow>
                )}
                {visibleRecords.map((record) => (
                  <TableRow
                    hover
                    key={record.id}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell onClick={() => navigate(`/servis-kaydi/${record.id}`)} sx={{ fontWeight: 600, color: 'text.primary' }}>{record.cihazSeriNumarasi || record.aracPlaka}</TableCell>
                    <TableCell onClick={() => navigate(`/servis-kaydi/${record.id}`)}>{record.musteriAdSoyad}</TableCell>
                    <TableCell onClick={() => navigate(`/servis-kaydi/${record.id}`)}><StatusChip label={record.durum} color={getStatusChipColor(record.durum)} /></TableCell>
                    <TableCell onClick={() => navigate(`/servis-kaydi/${record.id}`)}>{record.olusturmaTarihi?.toDate().toLocaleDateString('tr-TR')}</TableCell>
                    <TableCell onClick={() => navigate(`/servis-kaydi/${record.id}`)} sx={{ color: record.kalanTutar > 0 ? 'error.main' : 'inherit', fontWeight: 'bold', textAlign: 'right' }}>{formatCurrency(record.kalanTutar)}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Tooltip title="Görüntüle"><IconButton size="small" onClick={() => navigate(`/servis-kaydi/${record.id}`)}><VisibilityIcon /></IconButton></Tooltip>
                        {currentUser && (currentUser.role === 'Yönetici' || currentUser.role === 'Ofis Personeli') && (
                          <>
                            <Tooltip title="Düzenle"><IconButton size="small" color="info" onClick={() => navigate(`/servis-kaydi/duzenle/${record.id}`)}><EditIcon /></IconButton></Tooltip>
                            {canDelete() && (
                              <Tooltip title="Sil"><IconButton size="small" color="error" onClick={() => handleDelete(record.id, record.cihazSeriNumarasi)}><DeleteIcon /></IconButton></Tooltip>
                            )}
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {!loading && !isMobile && visibleCount < filteredRecords.length && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, py: 2 }}>
            <Typography variant="caption" color="text.secondary">{visibleRecords.length} / {filteredRecords.length} gösteriliyor</Typography>
            <Button variant="outlined" onClick={() => setVisibleCount(c => c + 25)}>Daha Fazla Yükle</Button>
          </Box>
        )}
      </GlassCard>
    </Box>
  );

};
export default ServisKayitlari;